/**
 * DrawerStackContext
 *
 * Manages the drawer stack state for the Layered Drawer System.
 * Provides a clean API for opening, closing, and querying drawers.
 *
 * Features:
 * - Stack of up to 3 drawers with z-index management
 * - Form state tracking for unsaved changes protection
 * - Animation state coordination
 * - Duplicate drawer prevention
 *
 * @see docs/stories/DRAWER-1.2-create-drawer-stack-context.md
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { toast } from "sonner";
import { UnsavedChangesDialog } from "@/components/ui/unsaved-changes-dialog";
import {
  announceDrawerOpened,
  announceDrawerClosed,
  announceMaxDrawersReached,
} from "@/utils/announcer";
import {
  trackDrawerOpen,
  trackDrawerClose,
  trackMaxDrawersReached,
  trackDuplicateDrawer,
} from "@/utils/drawerAnalytics";

// ============================================================================
// Constants
// ============================================================================

/** Maximum number of drawers that can be stacked */
export const MAX_DRAWERS = 3;

/** Animation duration in milliseconds */
export const ANIMATION_DURATION = 200;

// ============================================================================
// Types
// ============================================================================

/**
 * Supported drawer types.
 * Add new drawer types here as the system expands.
 */
export type DrawerType =
  // Core drawers (Phase 1-2)
  | "task" // TaskDrawer - create/view/edit tasks
  | "finance" // FinanceDrawer - financial details
  | "documents" // DocumentsDrawer - file management
  | "communication" // CommunicationDrawer - chat/comments
  | "notes" // NotesDrawer - project notes
  // Medium priority (Phase 3)
  | "consultations" // ConsultationsDrawer - booking/calendar
  | "site-survey" // SiteSurveyDrawer - survey details
  | "activity" // ActivityDrawer - timeline/history
  | "contracts" // ContractsDrawer - contract management
  | "proposals" // ProposalsDrawer - project proposals (EPIC-PROP-001)
  // Low priority (Phase 4)
  | "lender" // LenderDrawer - lender info
  | "designs" // DesignsDrawer - design requests
  | "project-management" // ProjectManagementDrawer - PM tab
  | "work-orders" // WorkOrdersDrawer - work order management
  | "partner" // PartnerDrawer - partner info
  | "sales-team" // SalesTeamDrawer - team assignments
  | "reports" // ReportsDrawer - reporting
  | "intake-form" // IntakeFormDrawer - new project form
  | "emails" // EmailsDrawer - email management
  | "contact"; // ContactQuickView - read-only contact info

/**
 * Width options for drawers
 */
export type DrawerWidth = "sm" | "md" | "lg" | "xl" | "full";

/**
 * Configuration when opening a drawer
 */
export interface DrawerConfig {
  /** Type of drawer to open */
  type: DrawerType;
  /** Optional custom title (uses default if not provided) */
  title?: string;
  /** Props to pass to the drawer component */
  props?: Record<string, unknown>;
  /** Drawer width */
  width?: DrawerWidth;
  /** Callback when drawer closes */
  onClose?: () => void;
}

/**
 * Form state tracked per drawer
 */
export interface DrawerFormState {
  /** Whether the form has unsaved changes */
  isDirty: boolean;
  /** Timestamp of last save */
  lastSaved?: number;
}

/**
 * Drawer instance stored in the stack
 */
export interface DrawerInstance {
  /** Unique identifier for this drawer instance */
  id: string;
  /** Type of drawer */
  type: DrawerType;
  /** Layer number (1, 2, or 3) */
  layer: number;
  /** Display title */
  title: string;
  /** Props passed to drawer component */
  props: Record<string, unknown>;
  /** Drawer width */
  width: DrawerWidth;
  /** Callback when drawer closes */
  onClose?: () => void;
  /** Timestamp when drawer was opened */
  openedAt: number;
  /** Preserved scroll position (for DRAWER-1.9) */
  scrollPosition?: number;
  /** Form state for unsaved changes tracking */
  formState?: DrawerFormState;
  /** Whether the drawer is in the process of closing (for exit animation) */
  isClosing?: boolean;
}

/**
 * Result of attempting to close a drawer with unsaved changes
 */
export interface CloseAttemptResult {
  /** Whether the close was blocked */
  blocked: boolean;
  /** Drawer ID that blocked the close (if any) */
  blockedBy?: string;
  /** Reason for blocking */
  reason?: "unsaved_changes";
}

/**
 * State for the unsaved changes confirmation dialog
 */
export interface ConfirmCloseDialogState {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** ID of the drawer that would be closed */
  drawerId: string | null;
  /** Title of the drawer (for display in dialog) */
  drawerTitle: string | null;
  /** Callback to confirm close (discard changes) */
  onConfirm: () => void;
  /** Callback to cancel close (return to drawer) */
  onCancel: () => void;
}

/**
 * Split view mode - how drawers are arranged
 */
export type SplitViewMode = "stack" | "split";

/**
 * Context value type
 */
export interface DrawerStackContextValue {
  // =========================================================================
  // State
  // =========================================================================

  /** Current stack of open drawers */
  drawers: DrawerInstance[];
  /** Topmost (active) drawer or null if none */
  activeDrawer: DrawerInstance | null;
  /** Current number of open drawers (0-3) */
  stackDepth: number;
  /** Whether an animation is in progress */
  isAnimating: boolean;
  /** Current view mode - stacked or side-by-side split */
  viewMode: SplitViewMode;
  /** Whether split view is available (requires exactly 2 active drawers and desktop width) */
  canSplitView: boolean;

  // =========================================================================
  // Operations
  // =========================================================================

  /**
   * Push a new drawer onto the stack.
   * Returns drawer ID if successful, empty string if blocked.
   */
  push: (config: DrawerConfig) => string;

  /**
   * Pop the topmost drawer from the stack.
   * Returns close attempt result for unsaved changes handling.
   */
  pop: (options?: { force?: boolean }) => CloseAttemptResult;

  /**
   * Pop all drawers from the stack.
   * Returns close attempt result for unsaved changes handling.
   */
  popAll: (options?: { force?: boolean }) => CloseAttemptResult;

  /**
   * Close a specific drawer by ID (and all above it).
   */
  close: (drawerId: string, options?: { force?: boolean }) => CloseAttemptResult;

  /**
   * Replace the top drawer with a new one (close + push in one operation)
   */
  replace: (config: DrawerConfig) => string;

  /**
   * Remove a drawer from the stack (called after animation completes).
   * This is an internal function used by GenericDrawer after exit animation.
   */
  removeDrawer: (drawerId: string) => void;

  // =========================================================================
  // Queries
  // =========================================================================

  /** Check if a drawer of the given type is currently open */
  isDrawerOpen: (type: DrawerType) => boolean;

  /** Find a drawer by type (returns null if not found) */
  findDrawer: (type: DrawerType) => DrawerInstance | null;

  /** Check if a new drawer can be opened (stack not full) */
  canOpenDrawer: () => boolean;

  /** Get drawer at a specific layer (1, 2, or 3) */
  getDrawerByLayer: (layer: number) => DrawerInstance | null;

  /** Get a drawer by its ID */
  getDrawerById: (id: string) => DrawerInstance | null;

  // =========================================================================
  // Form State
  // =========================================================================

  /** Register or update form state for a drawer */
  registerFormState: (drawerId: string, isDirty: boolean) => void;

  /** Check if any drawer has unsaved changes */
  hasUnsavedChanges: () => boolean;

  /** Get list of drawers with unsaved changes */
  getUnsavedDrawers: () => DrawerInstance[];

  // =========================================================================
  // Animation
  // =========================================================================

  /** Set animation state (used by StackedDrawer component) */
  setAnimating: (animating: boolean) => void;

  // =========================================================================
  // Unsaved Changes Dialog
  // =========================================================================

  /**
   * Request to close a drawer, showing confirmation if dirty.
   * This is the preferred way to close drawers from UI (backdrop, X button, Escape).
   */
  requestClose: (drawerId: string) => void;

  /**
   * Request to close all drawers, showing confirmation if any are dirty.
   */
  requestCloseAll: () => void;

  /** Current state of the confirmation dialog */
  confirmCloseDialog: ConfirmCloseDialogState;

  // =========================================================================
  // Focus & Animation
  // =========================================================================

  /** ID of drawer currently pulsing (for duplicate detection feedback) */
  pulsingDrawerId: string | null;

  /** Focus a specific drawer and optionally pulse it */
  focusDrawer: (drawerId: string, options?: { pulse?: boolean }) => void;

  /** Bring a drawer to the front (make it the topmost/active drawer) */
  bringToFront: (drawerId: string) => void;

  /** Whether close buttons should be highlighted (max drawer feedback) */
  highlightCloseButtons: boolean;

  // =========================================================================
  // Split View
  // =========================================================================

  /** Toggle between stack and split view modes */
  toggleViewMode: () => void;

  /** Set view mode directly */
  setViewMode: (mode: SplitViewMode) => void;
}

// ============================================================================
// Default Titles
// ============================================================================

const DEFAULT_TITLES: Record<DrawerType, string> = {
  // Core drawers
  task: "Task",
  finance: "Finance",
  documents: "Documents",
  proposals: "Proposals",
  communication: "Communication",
  notes: "Project Notes",
  // Medium priority
  consultations: "Consultations",
  "site-survey": "Site Survey",
  activity: "Activity",
  contracts: "Contracts",
  // Low priority
  lender: "Lender",
  designs: "Designs",
  "project-management": "Project Management",
  "work-orders": "Work Orders",
  partner: "Partner Info",
  "sales-team": "Sales Team",
  reports: "Reports",
  "intake-form": "Project Intake Form",
  emails: "Emails",
  contact: "Contact",
};

function getDefaultTitle(type: DrawerType): string {
  return DEFAULT_TITLES[type] || "Drawer";
}

// ============================================================================
// Context
// ============================================================================

const DrawerStackContext = createContext<DrawerStackContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface DrawerStackProviderProps {
  children: React.ReactNode;
}

export function DrawerStackProvider({ children }: DrawerStackProviderProps) {
  // =========================================================================
  // State
  // =========================================================================

  const [drawers, setDrawers] = useState<DrawerInstance[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const animationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Confirmation dialog state
  const [confirmCloseDialog, setConfirmCloseDialog] =
    useState<ConfirmCloseDialogState>({
      isOpen: false,
      drawerId: null,
      drawerTitle: null,
      onConfirm: () => {},
      onCancel: () => {},
    });

  // Pulse animation state
  const [pulsingDrawerId, setPulsingDrawerId] = useState<string | null>(null);
  const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Close button highlight state (for max drawer feedback)
  const [highlightCloseButtons, setHighlightCloseButtons] = useState(false);
  const highlightTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMaxFeedbackRef = useRef(0);
  const MAX_FEEDBACK_DEBOUNCE = 2000; // 2 seconds debounce

  // Split view state
  const [viewMode, setViewModeState] = useState<SplitViewMode>("stack");

  // =========================================================================
  // Computed Values
  // =========================================================================

  const activeDrawer = useMemo(
    () => (drawers.length > 0 ? drawers[drawers.length - 1] : null),
    [drawers]
  );

  const stackDepth = drawers.length;

  // Check if split view is available (exactly 2 active drawers)
  const activeDrawerCount = useMemo(
    () => drawers.filter((d) => !d.isClosing).length,
    [drawers]
  );

  const canSplitView = activeDrawerCount === 2;

  // =========================================================================
  // ID Generation
  // =========================================================================

  const generateId = useCallback((): string => {
    return `drawer-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
  }, []);

  // =========================================================================
  // Animation Management
  // =========================================================================

  const startAnimation = useCallback(() => {
    setIsAnimating(true);

    // Clear any existing timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // Auto-unlock after animation duration
    animationTimeoutRef.current = setTimeout(() => {
      setIsAnimating(false);
    }, ANIMATION_DURATION);
  }, []);

  // =========================================================================
  // Focus & Animation
  // =========================================================================

  const focusDrawer = useCallback(
    (drawerId: string, options?: { pulse?: boolean }): void => {
      const drawerElement = document.getElementById(drawerId);
      if (!drawerElement) return;

      // Scroll drawer into view if needed
      drawerElement.scrollIntoView({ behavior: "smooth", block: "nearest" });

      // Focus first focusable element
      const firstFocusable = drawerElement.querySelector(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      ) as HTMLElement;

      if (firstFocusable) {
        firstFocusable.focus();
      }

      // Optionally pulse the drawer
      if (options?.pulse) {
        // Clear any existing pulse timeout
        if (pulseTimeoutRef.current) {
          clearTimeout(pulseTimeoutRef.current);
        }

        setPulsingDrawerId(drawerId);

        // Clear pulse after animation
        pulseTimeoutRef.current = setTimeout(() => {
          setPulsingDrawerId(null);
        }, 300);
      }
    },
    []
  );

  // =========================================================================
  // Bring to Front
  // =========================================================================

  const bringToFront = useCallback(
    (drawerId: string): void => {
      setDrawers((prev) => {
        const index = prev.findIndex((d) => d.id === drawerId);
        if (index === -1) return prev;

        // Already at front
        if (index === prev.length - 1) return prev;

        // Don't reorder closing drawers
        const drawer = prev[index];
        if (drawer.isClosing) return prev;

        // Move drawer to end (top of stack) and recalculate layers
        const newDrawers = [...prev];
        const [removed] = newDrawers.splice(index, 1);
        newDrawers.push(removed);

        // Recalculate layers for active (non-closing) drawers
        let layerCounter = 1;
        return newDrawers.map((d) => {
          if (d.isClosing) return d;
          const newLayer = layerCounter++;
          return d.layer !== newLayer ? { ...d, layer: newLayer } : d;
        });
      });

      // Focus the drawer after reordering
      requestAnimationFrame(() => {
        focusDrawer(drawerId);
      });
    },
    [focusDrawer]
  );

  // =========================================================================
  // Max Drawer Feedback
  // =========================================================================

  const showMaxDrawerFeedback = useCallback(() => {
    // Debounce to prevent toast spam
    const now = Date.now();
    if (now - lastMaxFeedbackRef.current < MAX_FEEDBACK_DEBOUNCE) {
      return;
    }
    lastMaxFeedbackRef.current = now;

    // Show toast
    toast.warning("Close a drawer first to open another", {
      duration: 4000,
    });

    // Highlight close buttons
    if (highlightTimeoutRef.current) {
      clearTimeout(highlightTimeoutRef.current);
    }
    setHighlightCloseButtons(true);
    highlightTimeoutRef.current = setTimeout(() => {
      setHighlightCloseButtons(false);
    }, 1500);
  }, []);

  // =========================================================================
  // Operations
  // =========================================================================

  const push = useCallback(
    (config: DrawerConfig): string => {
      // Get only active drawers (not closing) for checks
      const activeDrawers = drawers.filter((d) => !d.isClosing);
      const hasClosingDrawers = drawers.some((d) => d.isClosing);

      // Block during animation only if we have active drawers animating in
      // (Don't block if we're only closing drawers - that shouldn't prevent new opens)
      if (isAnimating && !hasClosingDrawers) {
        return "";
      }

      // Check max limit (only count active drawers)
      if (activeDrawers.length >= MAX_DRAWERS) {
        showMaxDrawerFeedback();
        announceMaxDrawersReached();
        trackMaxDrawersReached();
        return "";
      }

      // Check for duplicate type (only among active drawers)
      const existingDrawer = activeDrawers.find((d) => d.type === config.type);
      if (existingDrawer) {
        // Focus and pulse the existing drawer
        focusDrawer(existingDrawer.id, { pulse: true });

        // Show toast notification
        toast.info(`${existingDrawer.title} is already open`, {
          duration: 2000,
        });

        // Track duplicate attempt
        trackDuplicateDrawer(config.type);

        return existingDrawer.id;
      }

      // Create new drawer instance
      // Layer is based on active drawers count, not total (including closing)
      const id = generateId();
      const instance: DrawerInstance = {
        id,
        type: config.type,
        layer: activeDrawers.length + 1,
        title: config.title || getDefaultTitle(config.type),
        props: config.props || {},
        width: config.width || "lg",
        onClose: config.onClose,
        openedAt: Date.now(),
      };

      // Start animation and update state
      startAnimation();
      setDrawers((prev) => [...prev, instance]);

      // Announce to screen readers
      announceDrawerOpened(instance.title, instance.layer);

      // Track analytics
      trackDrawerOpen({
        drawerType: instance.type,
        layer: instance.layer,
        projectId: config.props?.projectId as string | undefined,
        source: "programmatic",
      });

      return id;
    },
    [drawers, isAnimating, generateId, startAnimation, focusDrawer, showMaxDrawerFeedback]
  );

  const pop = useCallback(
    (options?: { force?: boolean }): CloseAttemptResult => {
      // Block during animation (unless forced)
      if (isAnimating && !options?.force) {
        return { blocked: true, reason: "unsaved_changes" };
      }

      if (drawers.length === 0) {
        return { blocked: false };
      }

      const topDrawer = drawers[drawers.length - 1];

      // Check for unsaved changes (unless forced)
      if (!options?.force && topDrawer.formState?.isDirty) {
        return {
          blocked: true,
          blockedBy: topDrawer.id,
          reason: "unsaved_changes",
        };
      }

      // Close the drawer
      startAnimation();
      topDrawer.onClose?.();
      setDrawers((prev) => prev.slice(0, -1));

      // Announce to screen readers
      announceDrawerClosed(topDrawer.title);

      // Track analytics
      trackDrawerClose(
        topDrawer.type,
        topDrawer.layer,
        topDrawer.formState?.isDirty ?? false,
        options?.force ?? false
      );

      return { blocked: false };
    },
    [drawers, isAnimating, startAnimation]
  );

  const popAll = useCallback(
    (options?: { force?: boolean }): CloseAttemptResult => {
      if (isAnimating && !options?.force) {
        return { blocked: true };
      }

      if (drawers.length === 0) {
        return { blocked: false };
      }

      // Check for unsaved changes (unless forced)
      if (!options?.force) {
        const dirtyDrawer = drawers.find((d) => d.formState?.isDirty);
        if (dirtyDrawer) {
          return {
            blocked: true,
            blockedBy: dirtyDrawer.id,
            reason: "unsaved_changes",
          };
        }
      }

      // Close all drawers
      startAnimation();
      drawers.forEach((d) => d.onClose?.());
      setDrawers([]);

      return { blocked: false };
    },
    [drawers, isAnimating, startAnimation]
  );

  const close = useCallback(
    (drawerId: string, options?: { force?: boolean }): CloseAttemptResult => {
      const index = drawers.findIndex((d) => d.id === drawerId);
      if (index === -1) {
        return { blocked: false };
      }

      // Don't allow closing if already closing
      const drawer = drawers[index];
      if (drawer.isClosing) {
        return { blocked: false };
      }

      if (isAnimating && !options?.force) {
        return { blocked: true };
      }

      // With automatic split view, we never cascade closes anymore.
      // Each drawer closes independently. This ensures clean transitions
      // between stack (1 or 3 drawers) and split (2 drawers) modes.
      const drawersToClose = [drawer];

      // Check for unsaved changes in any of them (unless forced)
      if (!options?.force) {
        const dirtyDrawer = drawersToClose.find((d) => d.formState?.isDirty);
        if (dirtyDrawer) {
          return {
            blocked: true,
            blockedBy: dirtyDrawer.id,
            reason: "unsaved_changes",
          };
        }
      }

      // For single drawer: use isClosing to trigger exit animation
      // For multiple drawers: use immediate removal to avoid split view transition issues
      drawersToClose.forEach((d) => d.onClose?.());

      if (drawers.length === 1) {
        // Single drawer - mark as closing to trigger exit animation
        // GenericDrawer will call removeDrawer() after animation completes
        setDrawers((prev) =>
          prev.map((d) =>
            drawersToClose.some((dc) => dc.id === d.id)
              ? { ...d, isClosing: true }
              : d
          )
        );
      } else {
        // Multiple drawers - use immediate removal to avoid animation conflicts
        // when transitioning between split and stack view modes
        setDrawers((prev) => {
          const filtered = prev.filter((d) => !drawersToClose.some((dc) => dc.id === d.id));
          // Recalculate layers for remaining drawers
          let layerCounter = 1;
          return filtered.map((d) => {
            if (d.isClosing) return d;
            const newLayer = layerCounter++;
            return d.layer !== newLayer ? { ...d, layer: newLayer } : d;
          });
        });
      }

      // Announce to screen readers
      announceDrawerClosed(drawer.title);

      // Track analytics
      trackDrawerClose(
        drawer.type,
        drawer.layer,
        drawer.formState?.isDirty ?? false,
        options?.force ?? false
      );

      return { blocked: false };
    },
    [drawers, isAnimating]
  );

  // Remove a drawer from the stack (called after animation completes)
  // Also recalculates layers for remaining drawers
  const removeDrawer = useCallback(
    (drawerId: string) => {
      setDrawers((prev) => {
        const filtered = prev.filter((d) => d.id !== drawerId);
        // Recalculate layers for remaining active (non-closing) drawers
        let layerCounter = 1;
        return filtered.map((d) => {
          if (d.isClosing) {
            // Keep closing drawers at their current layer
            return d;
          }
          const newLayer = layerCounter++;
          return d.layer !== newLayer ? { ...d, layer: newLayer } : d;
        });
      });
    },
    []
  );

  const replace = useCallback(
    (config: DrawerConfig): string => {
      // Pop current drawer first (forced)
      if (drawers.length > 0) {
        const topDrawer = drawers[drawers.length - 1];
        topDrawer.onClose?.();
        setDrawers((prev) => prev.slice(0, -1));
      }

      // Push new drawer (use internal logic to avoid duplicate check)
      const id = generateId();
      const instance: DrawerInstance = {
        id,
        type: config.type,
        layer: drawers.length, // Same layer as replaced drawer
        title: config.title || getDefaultTitle(config.type),
        props: config.props || {},
        width: config.width || "lg",
        onClose: config.onClose,
        openedAt: Date.now(),
      };

      startAnimation();
      setDrawers((prev) => [...prev.slice(0, -1), instance]);

      return id;
    },
    [drawers, generateId, startAnimation]
  );

  // =========================================================================
  // Queries
  // =========================================================================

  const isDrawerOpen = useCallback(
    (type: DrawerType): boolean => {
      // Only consider active drawers (not closing)
      return drawers.some((d) => d.type === type && !d.isClosing);
    },
    [drawers]
  );

  const findDrawer = useCallback(
    (type: DrawerType): DrawerInstance | null => {
      // Only find among active drawers (not closing)
      return drawers.find((d) => d.type === type && !d.isClosing) || null;
    },
    [drawers]
  );

  const canOpenDrawer = useCallback((): boolean => {
    // Only count active drawers (not closing)
    const activeCount = drawers.filter((d) => !d.isClosing).length;
    return activeCount < MAX_DRAWERS && !isAnimating;
  }, [drawers, isAnimating]);

  const getDrawerByLayer = useCallback(
    (layer: number): DrawerInstance | null => {
      return drawers.find((d) => d.layer === layer) || null;
    },
    [drawers]
  );

  const getDrawerById = useCallback(
    (id: string): DrawerInstance | null => {
      return drawers.find((d) => d.id === id) || null;
    },
    [drawers]
  );

  // =========================================================================
  // Form State Management
  // =========================================================================

  const registerFormState = useCallback(
    (drawerId: string, isDirty: boolean): void => {
      setDrawers((prev) =>
        prev.map((d) =>
          d.id === drawerId
            ? {
                ...d,
                formState: {
                  ...d.formState,
                  isDirty,
                  lastSaved: isDirty ? d.formState?.lastSaved : Date.now(),
                },
              }
            : d
        )
      );
    },
    []
  );

  const hasUnsavedChanges = useCallback((): boolean => {
    return drawers.some((d) => d.formState?.isDirty);
  }, [drawers]);

  const getUnsavedDrawers = useCallback((): DrawerInstance[] => {
    return drawers.filter((d) => d.formState?.isDirty);
  }, [drawers]);

  // =========================================================================
  // Split View
  // =========================================================================

  const toggleViewMode = useCallback(() => {
    if (!canSplitView && viewMode === "stack") {
      // Can't enter split view without exactly 2 drawers
      toast.info("Open exactly 2 drawers to use split view", { duration: 2000 });
      return;
    }
    setViewModeState((prev) => (prev === "stack" ? "split" : "stack"));
  }, [canSplitView, viewMode]);

  const setViewMode = useCallback((mode: SplitViewMode) => {
    if (mode === "split" && !canSplitView) {
      toast.info("Open exactly 2 drawers to use split view", { duration: 2000 });
      return;
    }
    setViewModeState(mode);
  }, [canSplitView]);

  // Automatic view mode based on drawer count:
  // - 2 drawers = split view
  // - 1 or 3 drawers = stack view
  useEffect(() => {
    if (canSplitView && viewMode !== "split") {
      setViewModeState("split");
    } else if (!canSplitView && viewMode === "split") {
      setViewModeState("stack");
    }
  }, [canSplitView, viewMode]);

  // =========================================================================
  // Unsaved Changes Dialog
  // =========================================================================

  const closeConfirmDialog = useCallback(() => {
    setConfirmCloseDialog({
      isOpen: false,
      drawerId: null,
      drawerTitle: null,
      onConfirm: () => {},
      onCancel: () => {},
    });
  }, []);

  const requestClose = useCallback(
    (drawerId: string): void => {
      const drawerIndex = drawers.findIndex((d) => d.id === drawerId);
      if (drawerIndex === -1) return;

      const drawer = drawers[drawerIndex];

      // Get all drawers from this one up (closing cascades)
      const drawersToClose = drawers.slice(drawerIndex);

      // Check for unsaved changes in any of them
      const dirtyDrawer = drawersToClose.find((d) => d.formState?.isDirty);

      if (dirtyDrawer) {
        // Show confirmation dialog
        setConfirmCloseDialog({
          isOpen: true,
          drawerId: drawer.id,
          drawerTitle: dirtyDrawer.title,
          onConfirm: () => {
            // Force close - use close() to trigger animation
            close(drawerId, { force: true });
            closeConfirmDialog();
          },
          onCancel: closeConfirmDialog,
        });
      } else {
        // No dirty forms, close directly with animation
        close(drawerId);
      }
    },
    [drawers, close, closeConfirmDialog]
  );

  const requestCloseAll = useCallback((): void => {
    if (drawers.length === 0) return;

    // Check for unsaved changes in any drawer
    const dirtyDrawer = drawers.find((d) => d.formState?.isDirty);

    if (dirtyDrawer) {
      // Show confirmation dialog
      setConfirmCloseDialog({
        isOpen: true,
        drawerId: drawers[0].id, // Target the first drawer to close all
        drawerTitle: dirtyDrawer.title,
        onConfirm: () => {
          // Force close all drawers - close the first one (cascades to all)
          close(drawers[0].id, { force: true });
          closeConfirmDialog();
        },
        onCancel: closeConfirmDialog,
      });
    } else {
      // No dirty forms, close all directly with animation
      // Close the first drawer which cascades to close all above it
      close(drawers[0].id);
    }
  }, [drawers, close, closeConfirmDialog]);

  // =========================================================================
  // Cleanup
  // =========================================================================

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (pulseTimeoutRef.current) {
        clearTimeout(pulseTimeoutRef.current);
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  // =========================================================================
  // Context Value
  // =========================================================================

  const value = useMemo<DrawerStackContextValue>(
    () => ({
      // State
      drawers,
      activeDrawer,
      stackDepth,
      isAnimating,
      viewMode,
      canSplitView,

      // Operations
      push,
      pop,
      popAll,
      close,
      replace,
      removeDrawer,

      // Queries
      isDrawerOpen,
      findDrawer,
      canOpenDrawer,
      getDrawerByLayer,
      getDrawerById,

      // Form State
      registerFormState,
      hasUnsavedChanges,
      getUnsavedDrawers,

      // Animation
      setAnimating: setIsAnimating,

      // Unsaved Changes Dialog
      requestClose,
      requestCloseAll,
      confirmCloseDialog,

      // Focus & Animation
      pulsingDrawerId,
      focusDrawer,
      bringToFront,
      highlightCloseButtons,

      // Split View
      toggleViewMode,
      setViewMode,
    }),
    [
      drawers,
      activeDrawer,
      stackDepth,
      isAnimating,
      viewMode,
      canSplitView,
      push,
      pop,
      popAll,
      close,
      replace,
      removeDrawer,
      isDrawerOpen,
      findDrawer,
      canOpenDrawer,
      getDrawerByLayer,
      getDrawerById,
      registerFormState,
      hasUnsavedChanges,
      getUnsavedDrawers,
      requestClose,
      requestCloseAll,
      confirmCloseDialog,
      pulsingDrawerId,
      focusDrawer,
      bringToFront,
      highlightCloseButtons,
      toggleViewMode,
      setViewMode,
    ]
  );

  return (
    <DrawerStackContext.Provider value={value}>
      {children}
      <UnsavedChangesDialog
        isOpen={confirmCloseDialog.isOpen}
        onCancel={confirmCloseDialog.onCancel}
        onDiscard={confirmCloseDialog.onConfirm}
        drawerTitle={confirmCloseDialog.drawerTitle || undefined}
      />
    </DrawerStackContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access the drawer stack context.
 * Must be used within a DrawerStackProvider.
 *
 * @throws Error if used outside of DrawerStackProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { push, pop, canOpenDrawer } = useDrawerStack();
 *
 *   const handleOpenTask = () => {
 *     if (canOpenDrawer()) {
 *       push({ type: 'task', props: { taskId: '123' } });
 *     }
 *   };
 *
 *   return <button onClick={handleOpenTask}>Open Task</button>;
 * }
 * ```
 */
export function useDrawerStack(): DrawerStackContextValue {
  const context = useContext(DrawerStackContext);

  if (!context) {
    throw new Error(
      "useDrawerStack must be used within a DrawerStackProvider. " +
        "Wrap your component tree with <DrawerStackProvider>."
    );
  }

  return context;
}

// ============================================================================
// Re-exports for convenience
// ============================================================================

export { DrawerStackContext };
